import { ColumnInfo, ColumnInfoList, DisplaySetting } from 'components/shared/display_setting'
import { TableCell } from 'components/shared/widget'
import { GlobalStateContext } from 'contexts/global_state_context'
import { Dna } from 'entities/dna'
import { ReactNode, useContext, useMemo } from 'react'
import { Badge } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { Form } from 'utils/hooks'
import { DnaViewModel } from 'view_models/dna_view_model'
import { SampleViewModel } from 'view_models/sample_view_model'

export const dnaColumnInfoList = (form?: Form<Record<string, unknown>>): ColumnInfoList => {
    const globalState = useContext(GlobalStateContext)
    const values: ColumnInfo[] = [
        { name: 'cDNA ID', th: true, width: 180, form: form, orderAttr: 'dna_code', searchType: { name: 'DnaId', type: 'multiple_text' } },
        { name: '製品', th: true, width: 220 },
        {
            name: '作成者',
            th: true,
            width: 150,
            form: form,
            orderAttr: 'dna_staff_id',
            searchType: {
                name: 'dnaStaffId',
                type: 'multiple_select',
                selectItems: globalState.users.map((user) => ({ label: user.fullName!, value: user.id! })),
                defaultExpand: true,
            },
        },
        { name: '作成日', th: true, width: 120, align: 'center', form: form, orderAttr: 'dna_date', searchType: { name: 'DnaDate', type: 'date' } },
        { name: 'RUN ID', th: true, width: 120, align: 'center', form: form },
        { name: 'QC', th: true, width: 70, align: 'center', form: form, orderAttr: 'dna_is_qc_ok' },
        {
            name: 'LibraryLot',
            th: true,
            width: 120,
            form: form,
            orderAttr: 'dna_library_reagent_item_id',
            searchType: { name: 'library_reagent_item_id', type: 'multiple_text' },
        },
        {
            name: 'RT Spike-in',
            th: true,
            width: 120,
            form: form,
            orderAttr: 'dna_rt_spike_in_reagent_item_id',
            searchType: { name: 'rt_spike_in_reagent_item_id', type: 'multiple_text' },
        },
        {
            name: 'Free water',
            th: true,
            width: 120,
            form: form,
            orderAttr: 'dna_free_water_reagent_item_id',
            searchType: { name: 'dna_free_water_reagent_item_id', type: 'multiple_text' },
        },
        { name: 'LibraryNote', th: true, width: 300, form: form, orderAttr: 'dna_library_note', searchType: { name: 'LibraryNote', type: 'text' } },
        { name: 'エラー', th: true, width: 300, form: form, orderAttr: 'dna_error_note', searchType: { name: 'ErrorNote', type: 'text' } },
    ]

    return {
        tableName: 'cDNA',
        values: values,
    }
}

export type DnaTableDataProps = {
    dna?: Dna
    displaySetting: DisplaySetting
    isEmphasis?: boolean
}

export const DnaTableData = ({ dna, displaySetting, isEmphasis }: DnaTableDataProps): JSX.Element => {
    if (!dna) {
        return (
            <>
                {displaySetting.columnInfoList.values.map((_: ColumnInfo, index: number) => {
                    if (displaySetting.hiddenList.includes(index)) {
                        return <></>
                    } else {
                        return <TableCell key={index}></TableCell>
                    }
                })}
            </>
        )
    }
    const vm = new DnaViewModel(dna)

    const createCells = useMemo(() => {
        const sampleVm = new SampleViewModel(dna.extraction!.sample!)
        return (
            <>
                <TableCell>{isEmphasis ? <Badge bg="warning">{dna.code}</Badge> : dna.code}</TableCell>
                <TableCell>{sampleVm.productItemView(true)}</TableCell>
                <TableCell>{dna.user?.fullName}</TableCell>
                <TableCell align="center">{vm.dateText()}</TableCell>
                <TableCell>
                    {dna.pcrRunIds?.map((id) => (
                        <a key={'dna_run-' + id} style={{ marginRight: 3 }} href={`pcr_run/${id}`} target="_blank" rel="noreferrer">
                            {id}
                        </a>
                    ))}
                </TableCell>
                <TableCell align="center">{vm.qcStatus()}</TableCell>

                <TableCell>
                    {dna.libraryReagentItemId ? (
                        <Link to={`reagent_items?reagent_kind=library&id=${dna.libraryReagentItemId}`}>ID: {dna.libraryReagentItemId}</Link>
                    ) : (
                        dna.libraryLot
                    )}
                </TableCell>
                <TableCell>
                    {dna.rtSpikeInReagentItemId ? (
                        <Link to={`reagent_items?reagent_kind=rt_spike_in&id=${dna.rtSpikeInReagentItemId}`}>ID: {dna.rtSpikeInReagentItemId}</Link>
                    ) : (
                        dna.spikeIn
                    )}
                </TableCell>
                <TableCell>
                    {dna.dnaFreeWaterReagentItemId && (
                        <Link to={`reagent_items?reagent_kind=free_water&id=${dna.dnaFreeWaterReagentItemId}`}>ID: {dna.dnaFreeWaterReagentItemId}</Link>
                    )}
                </TableCell>
                <TableCell>{dna.libraryNote}</TableCell>
                <TableCell>{dna.errorNote}</TableCell>
            </>
        )
    }, [displaySetting.hiddenList])

    return createCells.props.children.filter((_: ReactNode, index: number) => !displaySetting.hiddenList?.includes(index)).map((cell: ReactNode) => cell)
}
